import React, { useState } from "react";
import axios from "axios";
import { MapPin, Phone, Mail, MessageSquare, Clock, Send, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function ContactForm() {
  const [activeFormType, setActiveFormType] = useState("ofertă"); // "ofertă" | "vizionare" | "contact"
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    event_type: "Nunți",
    date: "",
    guests: 100,
    message: ""
  });
  const [submitting, setSubmitting] = useState(false);

  const eventTypes = [
    "Nunți",
    "Botezuri",
    "Cununii",
    "Aniversări",
    "Petreceri Private",
    "Evenimente Corporate",
    "Altul"
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "guests" ? parseInt(value) || 0 : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const payload = {
        ...formData,
        type: activeFormType
      };

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/contact`,
        payload
      );

      toast.success(response.data.message || "Mesajul a fost trimis cu succes!");
      
      // Reset form
      setFormData({
        name: "",
        email: "",
        phone: "",
        event_type: "Nunți",
        date: "",
        guests: 100,
        message: ""
      });
    } catch (err) {
      console.error(err);
      toast.error(
        err.response?.data?.detail || "Eroare la trimiterea formularului. Vă rugăm să încercați din nou."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section 
      id="contact"
      data-testid="contact-section"
      className="py-24 sm:py-32 bg-[#0A0A0A] relative"
    >
      {/* Floating WhatsApp Button */}
      <a
        href="https://wa.me/40720820028?text=Bun%C4%83%20ziua!%20A%C8%99%20dori%20mai%20multe%20detalii%20despre%20sala%20Elysium%20Events."
        target="_blank"
        rel="noopener noreferrer"
        data-testid="whatsapp-floating-button"
        className="fixed bottom-6 right-6 bg-[#25D366] text-white p-4 rounded-full shadow-2xl hover:scale-110 active:scale-95 transition-all duration-300 z-40 flex items-center justify-center border border-white/10"
        title="Contact direct pe WhatsApp"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" fill="currentColor" viewBox="0 0 16 16">
          <path d="M13.601 2.326A7.854 7.854 0 0 0 7.994 0C3.627 0 .068 3.558.064 7.926c0 1.399.366 2.76 1.057 3.965L0 16l4.204-1.102a7.933 7.933 0 0 0 3.79.977h.004c4.368 0 7.926-3.558 7.93-7.93A7.898 7.854 0 0 0 13.6 2.326zM7.994 14.521a6.573 6.573 0 0 1-3.356-.92l-.24-.144-2.494.654.666-2.433-.156-.251a6.56 6.57 0 0 1-1.007-3.505c0-3.626 2.957-6.584 6.591-6.584a6.56 6.56 0 0 1 4.66 1.931 6.557 6.57 0 0 1 1.928 4.66c-.004 3.639-2.961 6.592-6.592 6.592zm3.69-4.98c-.202-.1-.1.195-.148-.248-.414-.207-.82-.411-.89-.448-.07-.035-.12-.055-.17.035-.05.09-.195.248-.24.3-.044.053-.09.058-.29.043-.2-.078-.845-.312-1.61-.994-.595-.53-.997-1.185-1.115-1.385-.117-.2-.012-.31.087-.41.09-.09.2-.21.3-.314.08-.1.11-.17.165-.285.055-.115.028-.217-.014-.303-.043-.085-.17-.412-.234-.567-.06-.144-.123-.124-.17-.124-.043-.002-.093-.002-.143-.002a.278.278 0 0 0-.2.093c-.066.073-.255.25-.255.6 0 .352.256.693.292.742.036.049.504.773 1.22 1.082.17.073.302.117.405.15.172.054.329.046.452.024.137-.024.42-.17.479-.335.058-.165.058-.306.04-.335-.016-.03-.06-.05-.16-.1l-.004-.001z"/>
        </svg>
      </a>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="text-center space-y-4 mb-20">
          <span className="text-xs font-semibold tracking-[0.3em] text-[#D4AF37] uppercase">
            PLANIFICĂ CU NOI
          </span>
          <h2 
            data-testid="contact-title"
            className="font-serif-lux text-3xl sm:text-5xl text-white font-medium tracking-tight"
          >
            Contact & Rezervări
          </h2>
          <div className="w-24 h-[1px] bg-[#D4AF37]/50 mx-auto mt-4" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
          
          {/* Left Column: Details & Google Map */}
          <div className="lg:col-span-5 space-y-8">
            <div className="space-y-6">
              <h3 className="font-serif-lux text-2xl text-white font-medium">
                Elysium Events Mansion
              </h3>
              <p className="text-sm text-white/60 leading-relaxed font-light">
                Te așteptăm în inima Transilvaniei pentru a planifica împreună cel mai frumos eveniment din viața ta. Vizitele la locație se fac exclusiv pe bază de programare telefonică sau prin formularul alăturat.
              </p>
            </div>

            {/* Contact Details cards */}
            <div className="space-y-4">
              <div className="flex items-center space-x-4 p-4 bg-[#121212] border border-white/5">
                <div className="p-3 bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/20">
                  <MapPin size={20} />
                </div>
                <div>
                  <h4 className="text-xs tracking-wider text-white/50 uppercase">Locație</h4>
                  <p data-testid="contact-address" className="text-sm font-semibold text-white">Sibiu, România</p>
                </div>
              </div>

              <div className="flex items-center space-x-4 p-4 bg-[#121212] border border-white/5">
                <div className="p-3 bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/20">
                  <Phone size={20} />
                </div>
                <div>
                  <h4 className="text-xs tracking-wider text-white/50 uppercase">Telefon</h4>
                  <p data-testid="contact-phone-number" className="text-sm font-semibold text-white">0720 820 028</p>
                </div>
              </div>

              <div className="flex items-center space-x-4 p-4 bg-[#121212] border border-white/5">
                <div className="p-3 bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/20">
                  <Mail size={20} />
                </div>
                <div>
                  <h4 className="text-xs tracking-wider text-white/50 uppercase">Email</h4>
                  <p data-testid="contact-email-address" className="text-sm font-semibold text-white">contact@elysiumevents.ro</p>
                </div>
              </div>
            </div>

            {/* Google Map Embed Placeholder */}
            <div 
              data-testid="contact-map-container"
              className="w-full h-64 overflow-hidden border border-white/10 relative"
            >
              <iframe
                title="Elysium Events Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d44521.84904576352!2d24.116666878484964!3d45.790040523091174!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x474c6788fd4ee1cf%3A0xbc1ec7bfb4344400!2sSibiu!5e0!3m2!1sro!2sro!4v1719000000000!5m2!1sro!2sro"
                className="w-full h-full border-0 filter grayscale invert opacity-80"
                allowFullScreen=""
                loading="lazy"
              />
            </div>
          </div>

          {/* Right Column: Dynamic Form */}
          <div className="lg:col-span-7 bg-[#121212] border border-[#D4AF37]/10 p-8 sm:p-10 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-[4px] bg-[#D4AF37]" />
            
            {/* Tab triggers for Form type */}
            <div 
              data-testid="contact-tabs"
              className="flex border-b border-white/10 mb-8"
            >
              <button
                id="tab-trigger-oferta"
                data-testid="contact-tab-offer"
                onClick={() => setActiveFormType("ofertă")}
                className={`flex-1 text-center pb-4 text-xs font-semibold tracking-widest uppercase transition-all duration-300 ${
                  activeFormType === "ofertă" 
                    ? "text-[#D4AF37] border-b-2 border-[#D4AF37]" 
                    : "text-white/55 border-b border-transparent hover:text-white"
                }`}
              >
                Solicită Ofertă
              </button>
              <button
                id="tab-trigger-vizionare"
                data-testid="contact-tab-viewing"
                onClick={() => setActiveFormType("vizionare")}
                className={`flex-1 text-center pb-4 text-xs font-semibold tracking-widest uppercase transition-all duration-300 ${
                  activeFormType === "vizionare" 
                    ? "text-[#D4AF37] border-b-2 border-[#D4AF37]" 
                    : "text-white/55 border-b border-transparent hover:text-white"
                }`}
              >
                Programare Vizionare
              </button>
              <button
                id="tab-trigger-contact"
                data-testid="contact-tab-quick"
                onClick={() => setActiveFormType("contact")}
                className={`flex-1 text-center pb-4 text-xs font-semibold tracking-widest uppercase transition-all duration-300 ${
                  activeFormType === "contact" 
                    ? "text-[#D4AF37] border-b-2 border-[#D4AF37]" 
                    : "text-white/55 border-b border-transparent hover:text-white"
                }`}
              >
                Contact Rapid
              </button>
            </div>

            {/* Contact Form */}
            <form 
              data-testid="contact-form"
              onSubmit={handleSubmit} 
              className="space-y-6"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {/* Name */}
                <div className="space-y-2">
                  <label className="text-xs tracking-widest uppercase text-white/70 font-semibold">Nume Complet</label>
                  <input
                    type="text"
                    name="name"
                    required
                    data-testid="contact-input-name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Popescu Andrei"
                    className="w-full bg-[#0A0A0A] border border-[#D4AF37]/20 focus:border-[#D4AF37] text-white px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-[#D4AF37] transition-colors"
                  />
                </div>

                {/* Phone */}
                <div className="space-y-2">
                  <label className="text-xs tracking-widest uppercase text-white/70 font-semibold">Număr Telefon</label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    data-testid="contact-input-phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="0720 000 000"
                    className="w-full bg-[#0A0A0A] border border-[#D4AF37]/20 focus:border-[#D4AF37] text-white px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-[#D4AF37] transition-colors"
                  />
                </div>
              </div>

              {/* Email */}
              <div className="space-y-2">
                <label className="text-xs tracking-widest uppercase text-white/70 font-semibold">Adresă E-mail</label>
                <input
                  type="email"
                  name="email"
                  required
                  data-testid="contact-input-email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="andrei@example.com"
                  className="w-full bg-[#0A0A0A] border border-[#D4AF37]/20 focus:border-[#D4AF37] text-white px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-[#D4AF37] transition-colors"
                />
              </div>

              {/* Form fields conditional on type */}
              {activeFormType !== "contact" && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                  {/* Event Type */}
                  <div className="space-y-2">
                    <label className="text-xs tracking-widest uppercase text-white/70 font-semibold">Tip Eveniment</label>
                    <select
                      name="event_type"
                      data-testid="contact-input-event-type"
                      value={formData.event_type}
                      onChange={handleInputChange}
                      className="w-full bg-[#0A0A0A] border border-[#D4AF37]/20 focus:border-[#D4AF37] text-white px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-[#D4AF37] transition-colors"
                    >
                      {eventTypes.map((type) => (
                        <option key={type} value={type} className="bg-[#121212]">{type}</option>
                      ))}
                    </select>
                  </div>

                  {/* Date */}
                  <div className="space-y-2">
                    <label className="text-xs tracking-widest uppercase text-white/70 font-semibold">Data Eveniment</label>
                    <input
                      type="date"
                      name="date"
                      required
                      data-testid="contact-input-date"
                      value={formData.date}
                      onChange={handleInputChange}
                      className="w-full bg-[#0A0A0A] border border-[#D4AF37]/20 focus:border-[#D4AF37] text-white px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-[#D4AF37] transition-colors"
                    />
                  </div>

                  {/* Guests */}
                  <div className="space-y-2">
                    <label className="text-xs tracking-widest uppercase text-white/70 font-semibold">Nr. Invitați</label>
                    <input
                      type="number"
                      name="guests"
                      min="1"
                      required
                      data-testid="contact-input-guests"
                      value={formData.guests}
                      onChange={handleInputChange}
                      className="w-full bg-[#0A0A0A] border border-[#D4AF37]/20 focus:border-[#D4AF37] text-white px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-[#D4AF37] transition-colors"
                    />
                  </div>
                </div>
              )}

              {/* Message */}
              <div className="space-y-2">
                <label className="text-xs tracking-widest uppercase text-white/70 font-semibold">
                  {activeFormType === "ofertă" 
                    ? "Detalii Suplimentare (cerințe speciale, meniu, etc.)" 
                    : activeFormType === "vizionare" 
                    ? "Ora preferată și detalii vizită" 
                    : "Mesajul dumneavoastră"}
                </label>
                <textarea
                  name="message"
                  required
                  rows="4"
                  data-testid="contact-input-message"
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder="Spuneți-ne mai multe despre planurile dumneavoastră..."
                  className="w-full bg-[#0A0A0A] border border-[#D4AF37]/20 focus:border-[#D4AF37] text-white px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-[#D4AF37] transition-colors resize-none"
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit-button"
                className="w-full bg-[#D4AF37] hover:bg-[#C5A880] text-black text-xs font-semibold tracking-widest uppercase py-4 transition-all duration-300 flex items-center justify-center space-x-2"
              >
                {submitting ? (
                  <span className="animate-pulse">Se trimite...</span>
                ) : (
                  <>
                    <Send size={14} />
                    <span>Trimite Solicitare</span>
                  </>
                )}
              </button>
            </form>
          </div>

        </div>
      </div>
    </section>
  );
}
